// src/main/Tenant.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Menu,
    Tooltip,
    Card,
    CardContent,
    CardActions,
    Tabs,
    Tab,
    Avatar,
    LinearProgress,
    Stack,
    Alert,
    Snackbar,
    FormControlLabel,
    Switch,
    Rating,
    Stepper,
    Step,
    StepLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Breadcrumbs,
    Link,
    Fab,
    Zoom,
    Badge,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';

// Import Timeline components from @mui/lab
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineDot,
    TimelineConnector,
    TimelineContent,
    TimelineOppositeContent,
} from '@mui/lab';

import {
    Delete,
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    Filter as FilterIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    Apartment as ApartmentIcon,
    Home as HomeIcon,
    LocationOn,
    Business,
    FormatListNumbered,
    PictureAsPdf,
    TableChart,
    GetApp,
    Print,
    MeetingRoom,
    Build,
    CheckCircle,
    Cancel,
    People,
    AttachMoney,
    TrendingUp,
    TrendingDown,
    MoreVert,
    Dashboard as DashboardIcon,
    List as ListIcon,
    Store as StoreIcon,
    Phone,
    Email,
    Person,
    Assignment,
    Map as MapIcon,
    ViewModule,
    ViewList,
    ExpandMore,
    NavigateNext,
    AddCircleOutline,
    RemoveCircleOutline,
    Warning,
    CheckBox,
    RadioButtonChecked,
    BedroomParent,
    Bathtub,
    SquareFoot,
    Kitchen,
    LocalParking,
    Elevator,
    Security,
    Wifi,
    AcUnit,
    LocalLaundryService,
    Pool,
    EventNote,
    Receipt,
    History,
    MoveToInbox,
    ExitToApp,
    Gavel,
    VerifiedUser,
    CreditCard,
    CalendarToday,
    Description,
    Note,
    AttachFile,
    CloudUpload
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIPropertyList,
    APIApartmentList,
    APIFloorUnitList,
    APITenantList,
    APITenantStore,
    APITenantUpdate,
    APITenantDelete,
    APITenantExportExcel,
    APITenantExportPDF,
    APITenantPrint,
    APITenantStats,
    APITenantHistory,
    APITenantAssignApartment,
    APITenantVacate
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorrState';
import LoadingState from '../../components/LoadingState';
import { usePermission } from '../../hooks/usePermission';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Tenant = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [viewMode, setViewMode] = useState('list'); // 'list', 'grid', 'timeline'
    const [statistics, setStatistics] = useState(null);
    const [properties, setProperties] = useState([]);
    const [availableApartments, setAvailableApartments] = useState([]);
    const [tenantHistory, setTenantHistory] = useState([]);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [vacateDialogOpen, setVacateDialogOpen] = useState(false);
    const [selectedApartment, setSelectedApartment] = useState('');

    // Form data for Tenant
    const [formData, setFormData] = useState({
        full_name: '',
        phone_number: '',
        email: '',
        monthly_rent: '',
        security_deposit_amount: '',
        assigned_apartment_id: '',
        tenant_status: 'active',
        id_number: '',
        gender: '',
        occupation: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relationship: '',
        notes: ''
    });

    const [filters, setFilters] = useState({
        tenant_status: '',
        property_id: '',
        assigned_apartment_id: '',
        min_rent: '',
        max_rent: '',
        search: ''
    });

    const [errors, setErrors] = useState({
        full_name: false,
        phone_number: false,
        monthly_rent: false
    });

    // Tenant status options
    const tenantStatusOptions = [
        { id: 'active', name: 'Active', color: 'success', icon: <VerifiedUser /> },
        { id: 'inactive', name: 'Inactive', color: 'default', icon: <Cancel /> },
        { id: 'moved_out', name: 'Moved Out', color: 'warning', icon: <ExitToApp /> },
        { id: 'evicted', name: 'Evicted', color: 'error', icon: <Gavel /> }
    ];

    // Gender options
    const genderOptions = [
        { id: 'male', name: 'Male' },
        { id: 'female', name: 'Female' },
        { id: 'other', name: 'Other' }
    ];

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Use the updated permission hook
    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchProperties();
        fetchTenants();
        fetchStatistics();
    }, [showToast]);

    // Fetch properties for dropdown
    const fetchProperties = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIPropertyList);
            if (result.success) {
                setProperties(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching properties:", error);
        }
    };

    // Fetch available apartments for assignment
    const fetchAvailableApartments = async (propertyId = null) => {
        try {
            const userBranchId = getUserDetails("branchId");
            const params = { occupancy_status: 'vacant' };
            if (propertyId) params.property_id = propertyId;
            
            const result = await apiService.list(userBranchId, APIFloorUnitList, params);
            if (result.success) {
                setAvailableApartments(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching available apartments:", error);
            setAvailableApartments([]);
        }
    };

    // Fetch tenant history
    const fetchTenantHistory = async (tenantId) => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listWithParams(userBranchId, APITenantHistory, { tenant_id: tenantId });
            if (result.success) {
                setTenantHistory(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching tenant history:", error);
            setTenantHistory([]);
        }
    };


    // Fetch tenants
    const fetchTenants = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APITenantList);

            if (result.success) {
                const tenants = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: tenants
                }));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch statistics
    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APITenantStats);
            if (result.success && result.data) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };
    
    const handlePrint = async (record) => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to print tenant details", "error");
            return;
        }
        
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.print(APITenantPrint, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file generated successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            showToast("Error", "Failed to generate PDF file", "error");
        }
    };
    
    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export tenant data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToExcel(APITenantExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export tenant data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToPDF(APITenantExportPDF, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    // Filter based on search term and filters
    const filteredData = pageState.pageData.filter(record => {
        const matchesSearch = searchTerm === '' ||
            record.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.phone_number?.includes(searchTerm) ||
            record.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.id_number?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = !filters.tenant_status || 
            record.tenant_status === filters.tenant_status;

        const matchesProperty = !filters.property_id || 
            record.property_id?.toString() === filters.property_id;

        const matchesApartment = !filters.assigned_apartment_id || 
            record.assigned_apartment_id?.toString() === filters.assigned_apartment_id;

        const matchesMinRent = !filters.min_rent ||
            (record.monthly_rent && record.monthly_rent >= parseFloat(filters.min_rent));

        const matchesMaxRent = !filters.max_rent ||
            (record.monthly_rent && record.monthly_rent <= parseFloat(filters.max_rent));

        return matchesSearch && matchesStatus && matchesProperty && 
               matchesApartment && matchesMinRent && matchesMaxRent;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add tenants", "error");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            full_name: '',
            phone_number: '',
            email: '',
            monthly_rent: '',
            security_deposit_amount: '',
            assigned_apartment_id: '',
            tenant_status: 'active',
            id_number: '',
            gender: '',
            occupation: '',
            emergency_contact_name: '',
            emergency_contact_phone: '',
            emergency_contact_relationship: '',
            notes: ''
        });
        setErrors({
            full_name: false,
            phone_number: false,
            monthly_rent: false
        });
        fetchAvailableApartments();
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenView = async (record) => {
        console.log("record"+JSON.stringify(record))
        setSelectedRecord(record);
        await fetchTenantHistory(record.id);
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
        setTenantHistory([]);
    };

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit tenants", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        setFormData({
            full_name: record.full_name || '',
            phone_number: record.phone_number || '',
            email: record.email || '',
            monthly_rent: record.monthly_rent || '',
            security_deposit_amount: record.security_deposit_amount || '',
            assigned_apartment_id: record.assigned_apartment_id || '',
            tenant_status: record.tenant_status || 'active',
            id_number: record.id_number || '',
            gender: record.gender || '',
            occupation: record.occupation || '',
            emergency_contact_name: record.emergency_contact_name || '',
            emergency_contact_phone: record.emergency_contact_phone || '',
            emergency_contact_relationship: record.emergency_contact_relationship || '',
            notes: record.notes || ''
        });
        fetchAvailableApartments();
        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete tenants", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleOpenAssignApartment = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to assign apartments", "error");
            return;
        }
        setSelectedRecord(record);
        setSelectedApartment('');
        fetchAvailableApartments();
        setAssignDialogOpen(true);
    };

    const handleCloseAssignApartment = () => {
        setAssignDialogOpen(false);
    };

    const handleOpenVacateApartment = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to vacate apartments", "error");
            return;
        }
        setSelectedRecord(record);
        setVacateDialogOpen(true);
    };

    const handleCloseVacateApartment = () => {
        setVacateDialogOpen(false);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            tenant_status: '',
            property_id: '',
            assigned_apartment_id: '',
            min_rent: '',
            max_rent: '',
            search: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    const validateForm = () => {
        if (!formData.full_name?.trim()) {
            showToast("Validation Error", "Full name is required", "warning");
            return false;
        }
        if (!formData.phone_number?.trim()) {
            showToast("Validation Error", "Phone number is required", "warning");
            return false;
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            showToast("Validation Error", "Please enter a valid email address", "warning");
            return false;
        }
        if (!formData.monthly_rent || formData.monthly_rent <= 0) {
            showToast("Validation Error", "Monthly rent is required and must be greater than 0", "warning");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add tenants", "error");
            return;
        }
        
        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update tenants", "error");
            return;
        }
        
        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete tenants", "error");
            return;
        }
        
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        if (postStatus !== "DELETE") {
            formDataToSend.append('full_name', formData.full_name);
            formDataToSend.append('phone_number', formData.phone_number);
            formDataToSend.append('email', formData.email || '');
            formDataToSend.append('monthly_rent', formData.monthly_rent);
            formDataToSend.append('security_deposit_amount', formData.security_deposit_amount || 0);
            formDataToSend.append('assigned_apartment_id', formData.assigned_apartment_id || '');
            formDataToSend.append('tenant_status', formData.tenant_status);
            formDataToSend.append('id_number', formData.id_number || '');
            formDataToSend.append('gender', formData.gender || '');
            formDataToSend.append('occupation', formData.occupation || '');
            formDataToSend.append('emergency_contact_name', formData.emergency_contact_name || '');
            formDataToSend.append('emergency_contact_phone', formData.emergency_contact_phone || '');
            formDataToSend.append('emergency_contact_relationship', formData.emergency_contact_relationship || '');
            formDataToSend.append('notes', formData.notes || '');
        }

        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APITenantStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APITenantUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APITenantDelete, formDataToSend);
            }

            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                handleCloseDelete();
                fetchTenants();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    const handleAssignApartment = async () => {
        if (!selectedApartment) {
            showToast("Validation Error", "Please select an apartment", "warning");
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('tenant_id', selectedRecord.id);
        formDataToSend.append('apartment_id', selectedApartment);

        try {
            const result = await apiService.create(APITenantAssignApartment, formDataToSend);
            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseAssignApartment();
                fetchTenants();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    const handleVacateApartment = async () => {
        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('tenant_id', selectedRecord.id);

        try {
            const result = await apiService.create(APITenantVacate, formDataToSend);
            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseVacateApartment();
                fetchTenants();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    const getStatusColor = (status) => {
        const statusMap = {
            'active': 'success',
            'inactive': 'default',
            'moved_out': 'warning',
            'evicted': 'error'
        };
        return statusMap[status] || 'default';
    };

    const getStatusIcon = (status) => {
        const statusMap = {
            'active': <VerifiedUser />,
            'inactive': <Cancel />,
            'moved_out': <ExitToApp />,
            'evicted': <Gavel />
        };
        return statusMap[status] || <Person />;
    };

    const getStatusText = (status) => {
        const option = tenantStatusOptions.find(opt => opt.id === status);
        return option ? option.name : status;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Render timeline view for tenant history
    const renderTimelineView = () => {
        if (viewMode !== 'timeline' || !selectedRecord) return null;
        
        if (tenantHistory.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <History sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No history available
                    </Typography>
                </Box>
            );
        }

        return (
            <Timeline position="alternate">
                {tenantHistory.map((event, index) => (
                    <TimelineItem key={index}>
                        <TimelineOppositeContent color="text.secondary">
                            {new Date(event.created_at).toLocaleDateString()}
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                            <TimelineDot color={event.type === 'assignment' ? 'primary' : event.type === 'payment' ? 'success' : 'warning'}>
                                {event.type === 'assignment' ? <MeetingRoom /> : event.type === 'payment' ? <Receipt /> : <ExitToApp />}
                            </TimelineDot>
                            {index < tenantHistory.length - 1 && <TimelineConnector />}
                        </TimelineSeparator>
                        <TimelineContent>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Typography variant="h6" component="span">
                                    {event.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {event.description}
                                </Typography>
                            </Paper>
                        </TimelineContent>
                    </TimelineItem>
                ))}
            </Timeline>
        );
    };

    // Render grid view
    const renderGridView = () => {
        if (viewMode !== 'grid') return null;
        
        const currentData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        
        if (currentData.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <People sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No tenants found
                    </Typography>
                </Box>
            );
        }

        return (
            <Grid container spacing={2} sx={{ p: 2 }}>
                {currentData.map((record) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={record.id}>
                        <Card sx={{ 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': { boxShadow: 6 }
                        }}>
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                        {record.full_name?.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Chip
                                        icon={getStatusIcon(record.tenant_status)}
                                        label={getStatusText(record.tenant_status)}
                                        color={getStatusColor(record.tenant_status)}
                                        size="small"
                                    />
                                </Box>
                                <Typography variant="h6" component="div" gutterBottom>
                                    {record.full_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    <Phone fontSize="small" /> {record.phone_number}
                                </Typography>
                                {record.email && (
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        <Email fontSize="small" /> {record.email}
                                    </Typography>
                                )}
                                <Typography variant="h6" sx={{ mt: 2, color: theme.palette.success.main }}>
                                    {formatCurrency(record.monthly_rent)}
                                    <Typography variant="caption" color="text.secondary">
                                        /month
                                    </Typography>
                                </Typography>
                                {record.apartment_number && (
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                        Floor Unit: {record.apartment_number}
                                    </Typography>
                                )}
                            </CardContent>
                            <CardActions>
                                <Button size="small" onClick={() => handleOpenView(record)}>
                                    <ViewIcon fontSize="small" /> View
                                </Button>
                                <Button size="small" onClick={() => handleOpenEdit(record)}>
                                    <EditIcon fontSize="small" /> Edit
                                </Button>
                                <Button size="small" color="error" onClick={() => handleOpenDelete(record)}>
                                    <Delete fontSize="small" /> Delete
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return (
            <LoadingState
                message="Loading Tenant Data..."
                variant="medical"
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchTenants}
                title="Unable to Load Tenant Data"
                variant="medical"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Statistics Cards */}
            {statistics && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Tenants</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        {statistics.total_tenants || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56 }}>
                                    <People sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Active Tenants</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                        {statistics.active_tenants || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                    <VerifiedUser sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Occupied Units</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                        {statistics.occupied_units || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 56, height: 56 }}>
                                    <MeetingRoom sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Deposits</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                        {formatCurrency(statistics.total_deposits || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}>
                                    <AttachMoney sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Tenant Management ({recordsCount(pageState.pageData.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Add Tenant
                            </Button>
                        )}
                        
                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handlefilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                View Options
                            </Button>
                        )}
                        
                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Export Data
                            </Button>
                        )}
                        
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                                fetchTenants();
                                fetchStatistics();
                            }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                        >
                            Refresh
                        </Button>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => val && setViewMode(val)}
                            size="small"
                        >
                            <ToggleButton value="list">
                                <ViewList />
                            </ToggleButton>
                            <ToggleButton value="grid">
                                <ViewModule />
                            </ToggleButton>
                            <ToggleButton value="timeline">
                                <History />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search tenants..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.tenant_status || filters.property_id || filters.assigned_apartment_id || 
                  filters.min_rent || filters.max_rent) && (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.tenant_status && (
                            <Chip
                                label={`Status: ${getStatusText(filters.tenant_status)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, tenant_status: '' }))}
                                color={getStatusColor(filters.tenant_status)}
                                variant="outlined"
                            />
                        )}
                        {filters.property_id && (
                            <Chip
                                label={`Property: ${properties.find(p => p.id.toString() === filters.property_id)?.property_name || filters.property_id}`}
                                onDelete={() => setFilters(prev => ({ ...prev, property_id: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.min_rent && (
                            <Chip
                                label={`Min Rent: ${formatCurrency(filters.min_rent)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, min_rent: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.max_rent && (
                            <Chip
                                label={`Max Rent: ${formatCurrency(filters.max_rent)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, max_rent: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
            </Menu>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* List View */}
                {viewMode === 'list' && (
                    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Full Name</TableCell>
                                        <TableCell>Contact</TableCell>
                                        <TableCell>Monthly Rent (UGX)</TableCell>
                                        <TableCell>Floor Unit</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Created Date</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => (
                                        <TableRow
                                            key={record.id || index}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {record.full_name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Phone fontSize="small" /> {record.phone_number}
                                                    </Typography>
                                                    {record.email && (
                                                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Email fontSize="small" /> {record.email}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                                    {formatCurrency(record.monthly_rent)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {record.apartment_number ? (
                                                    <Chip
                                                        label={record.apartment_number}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">Not assigned</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getStatusIcon(record.tenant_status)}
                                                    label={getStatusText(record.tenant_status)}
                                                    color={getStatusColor(record.tenant_status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{formatCreatedAtDate(record.created_at)}</TableCell>
                                            <TableCell align="center">
                                                {hasPermission('VIEW') && (
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => handleOpenView(record)}
                                                            color="primary"
                                                        >
                                                            <ViewIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {hasPermission('PRINT') && (
                                                    <Tooltip title="Print">
                                                        <IconButton
                                                            onClick={() => handlePrint(record)}
                                                            color="info"
                                                            size="small"
                                                        >
                                                            <Print />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {hasPermission('EDIT') && (
                                                    <>
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                edge="end"
                                                                onClick={() => handleOpenEdit(record)}
                                                                color="secondary"
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {!record.assigned_apartment_id && record.tenant_status === 'active' && (
                                                            <Tooltip title="Assign Apartment">
                                                                <IconButton
                                                                    edge="end"
                                                                    onClick={() => handleOpenAssignApartment(record)}
                                                                    color="success"
                                                                >
                                                                    <MeetingRoom />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {record.assigned_apartment_id && record.tenant_status === 'active' && (
                                                            <Tooltip title="Vacate Apartment">
                                                                <IconButton
                                                                    edge="end"
                                                                    onClick={() => handleOpenVacateApartment(record)}
                                                                    color="warning"
                                                                >
                                                                    <ExitToApp />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}

                                                {hasPermission('DELETE') && (
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => handleOpenDelete(record)}
                                                            color="error"
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2 }}
                        />
                    </Paper>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && renderGridView()}

                {/* Timeline View */}
                {viewMode === 'timeline' && selectedRecord && renderTimelineView()}

                {filteredData.length === 0 && viewMode === 'list' && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 8,
                        color: 'text.secondary'
                    }}>
                        <People sx={{ fontSize: 60, mb: 2 }} />
                        <Typography variant="h6">No tenants found</Typography>
                        <Typography variant="body2">Try adjusting your search query</Typography>
                    </Box>
                )}
            </motion.div>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={handleCloseOptions}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                        >
                            Show All Tenants
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter Tenants
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Tenant Status</InputLabel>
                                    <Select
                                        name="tenant_status"
                                        value={filters.tenant_status}
                                        onChange={handleFilterChange}
                                        label="Tenant Status"
                                    >
                                        <MenuItem value="">All Status</MenuItem>
                                        {tenantStatusOptions.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Property</InputLabel>
                                    <Select
                                        name="property_id"
                                        value={filters.property_id}
                                        onChange={handleFilterChange}
                                        label="Property"
                                    >
                                        <MenuItem value="">All Properties</MenuItem>
                                        {properties.map((property) => (
                                            <MenuItem key={property.id} value={property.id}>
                                                {property.property_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Min Rent (UGX)"
                                    name="min_rent"
                                    type="number"
                                    value={filters.min_rent}
                                    onChange={handleFilterChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Max Rent (UGX)"
                                    name="max_rent"
                                    type="number"
                                    value={filters.max_rent}
                                    onChange={handleFilterChange}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">
                        Close
                    </Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Tenant Dialog */}
            <Dialog
                open={openAdd}
                onClose={handleCloseAdd}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{postStatus === "ADD" ? "Add New Tenant" : "Edit Tenant"}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                                    Personal Information
                                </Typography>
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Full Name *"
                                    name="full_name"
                                    value={formData.full_name}
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Person />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="ID Number"
                                    name="id_number"
                                    value={formData.id_number}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Badge />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Phone Number *"
                                    name="phone_number"
                                    value={formData.phone_number}
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Phone />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email (Optional)"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Gender</InputLabel>
                                    <Select
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleInputChange}
                                        label="Gender"
                                    >
                                        <MenuItem value="">Select Gender</MenuItem>
                                        {genderOptions.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Occupation"
                                    name="occupation"
                                    value={formData.occupation}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Assignment />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1, mt: 2 }}>
                                    Emergency Contact
                                </Typography>
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Emergency Contact Name"
                                    name="emergency_contact_name"
                                    value={formData.emergency_contact_name}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Emergency Contact Phone"
                                    name="emergency_contact_phone"
                                    value={formData.emergency_contact_phone}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Relationship"
                                    name="emergency_contact_relationship"
                                    value={formData.emergency_contact_relationship}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1, mt: 2 }}>
                                    Rental Information
                                </Typography>
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Monthly Rent (UGX) *"
                                    name="monthly_rent"
                                    type="number"
                                    value={formData.monthly_rent}
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AttachMoney />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Security Deposit (UGX)"
                                    name="security_deposit_amount"
                                    type="number"
                                    value={formData.security_deposit_amount}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CreditCard />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Tenant Status</InputLabel>
                                    <Select
                                        name="tenant_status"
                                        value={formData.tenant_status}
                                        onChange={handleInputChange}
                                        label="Tenant Status"
                                    >
                                        {tenantStatusOptions.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {item.icon}
                                                    {item.name}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Assign Apartment (Optional)</InputLabel>
                                    <Select
                                        name="assigned_apartment_id"
                                        value={formData.assigned_apartment_id}
                                        onChange={handleInputChange}
                                        label="Assign Apartment (Optional)"
                                    >
                                        <MenuItem value="">Not Assigned</MenuItem>
                                        {availableApartments.map((unit) => (
                                            <MenuItem key={unit.id} value={unit.id}>
                                                {unit.property_name} - Floor {unit.floor_number} - {unit.apartment_number}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={4}
                                    placeholder="Additional information about the tenant..."
                                />
                            </Grid>
                        </Box>

                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button color="error" onClick={handleCloseAdd} variant="contained">
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" variant="contained">
                                {postStatus === "ADD" ? "Save Tenant" : "Update Tenant"}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={handleCloseView}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Tenant Details</b>
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box>
                            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                                <Tab label="Personal Info" icon={<Person />} iconPosition="start" />
                                <Tab label="Rental Info" icon={<AttachMoney />} iconPosition="start" />
                                <Tab label="Emergency Contact" icon={<Phone />} iconPosition="start" />
                                <Tab label="History" icon={<History />} iconPosition="start" />
                            </Tabs>


                            {activeTab === 0 && (
                                <Box sx={{ p: 2 }}>
                                    <Box>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="h6" color="primary">
                                                    {selectedRecord.full_name}
                                                </Typography>
                                                <Chip
                                                    icon={getStatusIcon(selectedRecord.tenant_status)}
                                                    label={getStatusText(selectedRecord.tenant_status)}
                                                    color={getStatusColor(selectedRecord.tenant_status)}
                                                />
                                            </Box>
                                        </Grid><br/>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2"><b>Tenant ID:</b> {selectedRecord.tenant_id}</Typography>
                                        </Grid><br/>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2"><b>ID Number:</b> {selectedRecord.id_number || '-'}</Typography>
                                        </Grid><br/>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2"><b>Phone:</b> {selectedRecord.phone_number}</Typography>
                                        </Grid><br/>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2"><b>Email:</b> {selectedRecord.email || '-'}</Typography>
                                        </Grid><br/>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2"><b>Gender:</b> {selectedRecord.gender || '-'}</Typography>
                                        </Grid><br/>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2"><b>Occupation:</b> {selectedRecord.occupation || '-'}</Typography>
                                        </Grid>
                                    </Box>
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box sx={{ p: 2 }}>
                                    <Box>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <b>Monthly Rent:</b> {formatCurrency(selectedRecord.monthly_rent)}
                                            </Typography>
                                        </Grid><br/>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2">
                                                <b>Security Deposit:</b> {formatCurrency(selectedRecord.security_deposit_amount || 0)}
                                            </Typography>
                                        </Grid><br/>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2"><b>Assigned Apartment:</b> 
                                                {selectedRecord.apartment_number ? (
                                                    <Chip
                                                        label={`${selectedRecord.apartment_number} - ${selectedRecord.property_name || ''}`}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ ml: 1 }}
                                                    />
                                                ) : ' Not assigned'}
                                            </Typography>
                                        </Grid><br/>
                                        {selectedRecord.notes && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2"><b>Notes:</b> {selectedRecord.notes}</Typography>
                                            </Grid>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {activeTab === 2 && (
                                <Box sx={{ p: 2 }}>
                                    <Box>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2"><b>Emergency Contact Name:</b> {selectedRecord.emergency_contact_name || '-'}</Typography>
                                        </Grid><br/>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2"><b>Emergency Contact Phone:</b> {selectedRecord.emergency_contact_phone || '-'}</Typography>
                                        </Grid><br/>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2"><b>Relationship:</b> {selectedRecord.emergency_contact_relationship || '-'}</Typography>
                                        </Grid>
                                    </Box>
                                </Box>
                            )}

                            {activeTab === 3 && (
                                <Box sx={{ p: 2 }}>
                                    <Timeline position="alternate">
                                        {tenantHistory.map((event, index) => (
                                            <TimelineItem key={index}>
                                                <TimelineOppositeContent color="text.secondary">
                                                    {new Date(event.created_at).toLocaleDateString()}
                                                </TimelineOppositeContent>
                                                <TimelineSeparator>
                                                    <TimelineDot color={event.type === 'assignment' ? 'primary' : event.type === 'payment' ? 'success' : 'warning'}>
                                                        {event.type === 'assignment' ? <MeetingRoom /> : event.type === 'payment' ? <Receipt /> : <ExitToApp />}
                                                    </TimelineDot>
                                                    {index < tenantHistory.length - 1 && <TimelineConnector />}
                                                </TimelineSeparator>
                                                <TimelineContent>
                                                    <Paper elevation={2} sx={{ p: 2 }}>
                                                        <Typography variant="h6" component="span">
                                                            {event.title}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {event.description}
                                                        </Typography>
                                                    </Paper>
                                                </TimelineContent>
                                            </TimelineItem>
                                        ))}
                                    </Timeline>
                                </Box>
                            )}

                            <Grid item xs={12}>
                                <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                                    System Information
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2"><b>Created Date:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2"><b>Last Updated:</b> {formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">
                        Close
                    </Button>
                    {hasPermission('PRINT') && selectedRecord && (
                        <Button onClick={() => handlePrint(selectedRecord)} color="info" variant="contained">
                            <Print sx={{ mr: 1 }} /> Print
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Assign Apartment Dialog */}
            <Dialog
                open={assignDialogOpen}
                onClose={handleCloseAssignApartment}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Assign Apartment</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body1" gutterBottom>
                            Assign an apartment to <strong>{selectedRecord?.full_name}</strong>
                        </Typography>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Select Apartment</InputLabel>
                            <Select
                                value={selectedApartment}
                                onChange={(e) => setSelectedApartment(e.target.value)}
                                label="Select Apartment"
                            >
                                <MenuItem value="">Select an apartment</MenuItem>
                                {availableApartments.map((unit) => (
                                    <MenuItem key={unit.id} value={unit.id}>
                                        {unit.property_name} - Floor {unit.floor_number} - {unit.apartment_number} 
                                        ({formatCurrency(unit.monthly_rent_price)}/month)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseAssignApartment} variant="contained">
                        Cancel
                    </Button>
                    <Button onClick={handleAssignApartment} color="primary" variant="contained">
                        Assign Apartment
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Vacate Apartment Dialog */}
            <Dialog
                open={vacateDialogOpen}
                onClose={handleCloseVacateApartment}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Vacate Apartment</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to vacate the apartment for <strong>{selectedRecord?.full_name}</strong>?
                    </Typography>
                    <Alert severity="warning">
                        This will mark the apartment as vacant and the tenant as moved out.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseVacateApartment} variant="contained">
                        Cancel
                    </Button>
                    <Button onClick={handleVacateApartment} color="primary" variant="contained">
                        Confirm Vacate
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={handleCloseDelete}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Tenant</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this tenant?
                    </Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {selectedRecord.full_name}
                            <Box sx={{ mt: 1 }}>
                                <Chip
                                    icon={<Person />}
                                    label={selectedRecord.full_name}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            </Box>
                        </Typography>
                    )}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>
                        Warning: This action cannot be undone!
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseDelete} variant="contained">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Tenant;

